/**
 * Homepage v2 — shared interaction script.
 * Loaded (optionally, more than once) by hero-v2 / category-bar-v2 /
 * bestsellers-v2 sections. Guarded so re-execution is a no-op — safe even
 * if a merchant duplicates a section on the template.
 *
 * Uses event delegation on document instead of per-element inline
 * onclick handlers, so it keeps working with sections added/reordered/
 * removed live in the theme editor.
 */
(function () {
  if (window.__hv2Init) return;
  window.__hv2Init = true;

  // ---- Category bar: sync spacer height to the bar's real rendered height ----
  // The bar is position:fixed (position:sticky doesn't persist past its own
  // section's tiny wrapper div in Shopify's per-section DOM, so it can't
  // stay pinned while scrolling through the rest of the page) — a spacer
  // reserves its flow space instead. Measuring the real height here (rather
  // than hardcoding a guessed px value in CSS) avoids any gap/overlap drift
  // from font-rendering or OS differences.
  function syncCatbarSpacer() {
    var bar = document.querySelector('.hv2-catbar');
    var spacer = document.querySelector('.hv2-catbar__spacer');
    if (!bar || !spacer) return;
    spacer.style.height = bar.offsetHeight + 'px';
  }
  syncCatbarSpacer();
  window.addEventListener('load', syncCatbarSpacer);
  window.addEventListener('resize', syncCatbarSpacer);
  if (window.document.fonts && document.fonts.ready) {
    document.fonts.ready.then(syncCatbarSpacer);
  }

  // ---- Reel popup: play the already-uploaded video + shoppable Add to Cart ----
  // Prefers the merchant's own uploaded video file (plays instantly, no
  // external request) over Instagram's embed widget, which loads a
  // separate third-party script/iframe and is noticeably slower — only
  // used as a fallback for reels that don't have a video file yet. The
  // product panel is pre-rendered server-side in a <template> per reel
  // block (real Liquid data) and just cloned in — no client-side product
  // API calls.
  (function () {
    var modal = document.querySelector('[data-hv2-reel-modal]');
    if (!modal) return;
    var embedEl = modal.querySelector('[data-hv2-reel-embed]');
    var productSlot = modal.querySelector('[data-hv2-reel-product-slot]');
    var igScriptRequested = false;

    function loadInstagramEmbedScript(callback) {
      if (window.instgrm) { callback(); return; }
      document.addEventListener('hv2:ig-embed-ready', callback, { once: true });
      if (igScriptRequested) return;
      igScriptRequested = true;
      var script = document.createElement('script');
      script.src = '//www.instagram.com/embed.js';
      script.async = true;
      script.onload = function () {
        document.dispatchEvent(new CustomEvent('hv2:ig-embed-ready'));
      };
      document.body.appendChild(script);
    }

    function openReel(videoUrl, instagramUrl, panelId) {
      productSlot.innerHTML = '';
      var panel = panelId && document.getElementById(panelId);
      if (panel && 'content' in panel) {
        productSlot.appendChild(panel.content.cloneNode(true));
      }
      modal.hidden = false;
      document.body.style.overflow = 'hidden';

      if (videoUrl) {
        embedEl.innerHTML = '<video class="hv2-reel-modal__video" src="' + videoUrl + '" controls autoplay playsinline></video>';
      } else if (instagramUrl) {
        embedEl.innerHTML = '<blockquote class="instagram-media" data-instgrm-permalink="' + instagramUrl + '" data-instgrm-version="14" style="margin:0;width:100%;"></blockquote>';
        loadInstagramEmbedScript(function () {
          if (window.instgrm) window.instgrm.Embeds.process();
        });
      }
    }

    function closeReel() {
      modal.hidden = true;
      embedEl.innerHTML = '';
      document.body.style.overflow = '';
    }

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-hv2-reel-open]');
      if (opener) {
        openReel(
          opener.getAttribute('data-hv2-reel-video'),
          opener.getAttribute('data-hv2-reel-instagram'),
          opener.getAttribute('data-hv2-reel-panel')
        );
        return;
      }
      if (event.target.closest('[data-hv2-reel-close]')) {
        closeReel();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !modal.hidden) closeReel();
    });
  })();

  // ---- Category filter pills (data-hv2-filter-bar) ----
  // Each pill's data-hv2-filter-value is a lowercased product tag (blank for
  // "All"). Cards carry their own tags in data-hv2-filter-tags. Purely a
  // client-side show/hide — no reload, no server round-trip.
  document.querySelectorAll('[data-hv2-filter-bar]').forEach(function (bar) {
    var targetId = bar.getAttribute('data-hv2-filter-target');
    var track = targetId && document.getElementById(targetId);
    if (!track) return;
    var cards = Array.prototype.slice.call(track.querySelectorAll('[data-hv2-filter-tags]'));
    var buttons = Array.prototype.slice.call(bar.querySelectorAll('[data-hv2-filter-btn]'));

    function applyFilter(value) {
      // Deliberately does NOT touch --hv2-cols here: cards must keep their
      // original size from the "All" view regardless of how many match a
      // filter (merchant may only have 1-2 products in a category today
      // and add more later) — a filtered-out card just leaves blank grid
      // space rather than the remaining cards stretching to fill the row.
      cards.forEach(function (card) {
        var tags = (card.getAttribute('data-hv2-filter-tags') || '').split(',');
        var show = value === '' || tags.indexOf(value) !== -1;
        card.style.display = show ? '' : 'none';
      });
    }

    bar.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-hv2-filter-btn]');
      if (!btn) return;
      buttons.forEach(function (b) { b.classList.toggle('is-active', b === btn); });
      applyFilter(btn.getAttribute('data-hv2-filter-value') || '');
    });
  });

  // ---- Carousel arrows (data-hv2-scroll-target + data-hv2-scroll-dir) ----
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-hv2-scroll-dir]');
    if (!btn) return;
    var targetId = btn.getAttribute('data-hv2-scroll-target');
    var track = targetId && document.getElementById(targetId);
    if (!track) return;
    var dir = btn.getAttribute('data-hv2-scroll-dir') === 'prev' ? -1 : 1;
    var amount = Math.max(track.clientWidth * 0.8, 240);
    track.scrollBy({ left: dir * amount, behavior: 'smooth' });
  });

  // Keyboard support: focus a track, use Left/Right arrow keys to scroll.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    var track = event.target.closest('[data-hv2-scroll-track]');
    if (!track) return;
    var amount = Math.max(track.clientWidth * 0.8, 240);
    track.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * amount, behavior: 'smooth' });
  });

  // ---- Pagination dots (data-hv2-dots + data-hv2-dots-target) ----
  // Clicking a dot scrolls its track to that "page"; scrolling the track
  // updates which dot is marked active. Page width = track's own width,
  // so this adapts to however many cards actually fit per viewport.
  function initDots(dotsEl) {
    var targetId = dotsEl.getAttribute('data-hv2-dots-target');
    var track = targetId && document.getElementById(targetId);
    if (!track || dotsEl.dataset.hv2DotsInit) return;
    dotsEl.dataset.hv2DotsInit = 'true';
    var dots = Array.prototype.slice.call(dotsEl.querySelectorAll('[data-hv2-dot]'));

    function setActive(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
      });
    });

    var scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var page = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        setActive(Math.min(page, dots.length - 1));
      }, 80);
    });
  }
  document.querySelectorAll('[data-hv2-dots]').forEach(initDots);

  // ---- Add to Cart (AJAX Cart API, progressive enhancement) ----
  // Falls back to a normal form POST to /cart/add if fetch fails, so it
  // still works with JS disabled since the form's action/method are real.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('form[data-hv2-atc-form]');
    if (!form) return;
    event.preventDefault();

    var button = form.querySelector('[data-hv2-atc-button]');
    var formData = new FormData(form);

    if (button) {
      button.disabled = true;
      button.dataset.originalText = button.dataset.originalText || button.textContent;
      button.textContent = 'Adding…';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('hv2:cart:added', { detail: { form: form } }));
        document.dispatchEvent(new CustomEvent('ujc:open-cart'));
        if (button) button.textContent = 'Added ✓';
        // Refresh the cart count bubble if the theme exposes one.
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.querySelectorAll('[data-cart-count], .nav-cart-count').forEach(function (el) {
              el.textContent = cart.item_count;
              if (el.classList.contains('nav-cart-count')) {
                el.style.display = cart.item_count > 0 ? 'flex' : 'none';
              }
            });
          })
          .catch(function () {});
      })
      .catch(function () {
        // Progressive-enhancement fallback: real cart form POST.
        form.submit();
      })
      .finally(function () {
        if (button) {
          setTimeout(function () {
            button.disabled = false;
            button.textContent = button.dataset.originalText;
          }, 1400);
        }
      });
  });

  // ---- Hero carousel: arrows, dots, autoplay, swipe ----
  // Autoplay is skipped under prefers-reduced-motion and paused whenever the
  // tab isn't visible, so it never fights a merchant reading the slide or
  // burns cycles in a background tab.
  document.querySelectorAll('[data-hv2-hero-carousel]').forEach(function (carousel) {
    if (carousel.hasAttribute('data-hv2-hero-single')) return;
    var slides = Array.prototype.slice.call(carousel.querySelectorAll('[data-hv2-hero-slide]'));
    var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-hv2-hero-dot]'));
    if (slides.length < 2) return;
    var prevBtn = carousel.querySelector('[data-hv2-hero-prev]');
    var nextBtn = carousel.querySelector('[data-hv2-hero-next]');
    var index = 0;
    var timer = null;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (el, n) { el.classList.toggle('is-active', n === index); });
      dots.forEach(function (el, n) { el.classList.toggle('is-active', n === index); });
    }

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function start() {
      if (reduceMotion || document.hidden) return;
      stop();
      timer = setInterval(function () { show(index + 1); }, 6000);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { show(index - 1); start(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { show(index + 1); start(); });
    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () { show(n); start(); });
    });

    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    var touchStartX = null;
    carousel.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
      stop();
    }, { passive: true });
    carousel.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var delta = e.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) > 40) show(delta < 0 ? index + 1 : index - 1);
      start();
    }, { passive: true });

    show(0);
    start();
  });
})();
